import { IContraceptionResponse, IClinicAutoComplete } from "@interfaces";
import { memo, useEffect, useCallback, useState } from "react";
import * as Yup from "yup";
import { Input, Form, Tabs, Row, Col, DatePicker, AutoComplete } from "antd";
import { useYup } from "@validations";
import { useTranslation } from "react-i18next";
import { PATH_DEPO_RECORDS, enumAdminRole } from "@configs";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { setLoading, selectAuth } from "@redux";
import { contraceptionApi } from "@api";
import { useNotify } from "@utils";
import dayjs from "dayjs";
import { SharedButton } from "@components";
import styled from "styled-components";
import { ClientDoseForm } from "./ClientDoseForm";
import { doseCountForDMPA_IM, doseCountForOC_PILL, getTransformArrayToObject, getTransformObjectToArray } from "./contraceptionHelper";

interface IProps {
  contraception?: IContraceptionResponse;
}
const dateFormat = "DD-MM-YYYY";

export const ContraceptionForm = memo(({ contraception }: IProps) => {
  const lastMonth = dayjs().subtract(1, 'month').set('date', 25);
  const currentMonth =  (dayjs().isAfter(dayjs().set('date', 25), 'date') || dayjs().isSame(dayjs().set('date', 25), 'date')) ? dayjs().set('date', 25) : dayjs();
  const initValue: IContraceptionResponse = {
    contraceptionId: "",
    clinicId: "",
    clinicName: "",
    stateId: "",
    stateName: "",
    regionId: "",
    regionName: "",
    reportingFrom: lastMonth,
    reportingTo: currentMonth,
    submittedDate: "",
    submittedBy: "",
    contraception_clients: {
      dmpa_im_New_client_Under_25: 0,
      dmpa_im_New_client_Over_25: 0,
      dmpa_im_Old_client_Under_25: 0,
      dmpa_im_Old_client_Over_25: 0,
      oc_pill_New_client_Under_25: 0,
      oc_pill_New_client_Over_25: 0,
      oc_pill_Old_client_Under_25: 0,
      oc_pill_Old_client_Over_25: 0,
    }
  };
  const { YupContraception } = useYup();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { success, error } = useNotify();
  const { auth } = useSelector(selectAuth);
  const contraceptionSchema = Yup.object().shape(YupContraception);
  const [fetching, setFetching] = useState<boolean>(false);
  const [autoCompleteclinics, setAutocompleteClinics] = useState<IClinicAutoComplete[]>([]);
  const [data, setData] = useState<IContraceptionResponse>(initValue);
  const [clinicId, setClinicId] = useState<string>("");
  const [contraceptionForm] = Form.useForm();
  useEffect(() => {
    if (contraception) {
      const contraceptionClient = getTransformArrayToObject(contraception.contraception_clients);
      setData({ ...data, ...contraception, contraception_clients: contraceptionClient});
      contraceptionForm.setFieldsValue({
        ...data,
        ...contraception,
        reportingFrom: dayjs(contraception.reportingFrom),
        reportingTo: dayjs(contraception.reportingTo),
        submittedDate: dayjs(contraception.submittedDate),
      });
      setClinicId(contraception.clinicId || "");
    }else {
      if(auth?.userRole === enumAdminRole.CLINIC) {
        setClinicId(auth?.userId || "");
      }
      contraceptionForm.setFieldsValue({
        ...data,
        submittedDate: dayjs()
      });
    }
  }, [contraception]);

  const clinicVal: string = Form.useWatch("clinicName", contraceptionForm);

  useEffect(() => {
    if(clinicVal !== undefined) {
      searchClinic();
    }
  }, [clinicVal]);


  const searchClinic = useCallback(async () => {
    try {
      setFetching(true);
      const res = await contraceptionApi.autocompleteSearchClinic(clinicVal);
      setAutocompleteClinics(getClinicValues(res.data?.data));
      setFetching(false);
    } catch (err: any) {
      // error(err.message);
      setFetching(false);
    } finally {
      setFetching(false);
    }
  }, [clinicVal]);

  const getClinicValues = (data: IClinicAutoComplete[]) => {
    return data.reduce<IClinicAutoComplete[]>((a, c) => {
      a.push({ ...c, value: c?.clinicName || "" });
      return a;
    }, []);
  };

  const handleDMPA_IMClient = (client_info: string, doseCount: number) => {
    const clientData = doseCountForDMPA_IM(data.contraception_clients, client_info, doseCount);
    setData((prevData) => {
      return {...prevData, contraception_clients: clientData};
    });
  };

  const handleOCPillsClient = (client_info: string, doseCount: number) => {
    const clientData = doseCountForOC_PILL(data.contraception_clients, client_info, doseCount);
    setData((prevData) => {
      return {...prevData, contraception_clients: clientData};
    });
  };

  const onFinish = async (values: IContraceptionResponse) => {
    try {
      dispatch(setLoading(true));
      const clientInfos = getTransformObjectToArray(data.contraception_clients);
      if (contraception) {
        await contraceptionApi.update(
          {
            submittedBy: values.submittedBy,
            clinicId: clinicId, 
            reportingFrom: dayjs(values.reportingFrom).format(), 
            reportingTo: dayjs(values.reportingTo).format(), 
            submittedDate: dayjs(values.submittedDate).format(),
            contraceptionClients: clientInfos
          },
          data?.contraceptionId || ""
        );
      } else {
        await contraceptionApi.create(
          {...values, 
            clinicId: clinicId, 
            reportingFrom: dayjs(values.reportingFrom).format(), 
            reportingTo: dayjs(values.reportingTo).format(), 
            submittedDate: dayjs(values.submittedDate).format(),
            contraceptionClients: clientInfos
        });
      }
      success(t("message.create.success"));
      history.push(PATH_DEPO_RECORDS);
    } catch (err: any) {
      error(err.message);
      dispatch(setLoading(false));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log("Failed:", errorInfo);
  };

  const yupSync = {
    async validator({ field }: any, value: any) {
      await contraceptionSchema.validateSyncAt(field, { [field]: value });
    },
  };
  
  const handleSearchClinic = (value: string) => {
    contraceptionForm.setFieldsValue({ clinicId: value });
  };

  const onSelectClinic = (value: string, option: IClinicAutoComplete) => {
    setClinicId(option?._id || "");
  };

  const goBack = () => history.goBack();

  return (
    <Form
      form={contraceptionForm}
      name="contraception_form"
      initialValues={data}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      layout="vertical"
    >
      {
        auth?.userRole !== enumAdminRole.CLINIC && (
          <Row gutter={{ md: 54, lg: 54 }}>
            <Col lg={12} md={24} sm={24} xs={24}>
              <Form.Item label={t("page.provider_name")} name="clinicName" rules={[yupSync]}>
                <AutoComplete
                  options={autoCompleteclinics}
                  onSelect={onSelectClinic}
                  onSearch={handleSearchClinic}
                  placeholder="type program name"
                />
              </Form.Item>
            </Col>
            <Col lg={12} md={24} sm={24} xs={24}>
            </Col>
          </Row>

        )
      }
      <Row gutter={{ md: 54, lg: 54 }}>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.reporting_from")} name="reportingFrom" rules={[yupSync]}>
            <DatePicker format={dateFormat} />
          </Form.Item>
        </Col>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.reporting_to")} name="reportingTo" rules={[yupSync]}>
            <DatePicker format={dateFormat} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={{ md: 54, lg: 54 }}>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.submitted_by")} name="submittedBy" rules={[yupSync]}>
            <Input />
          </Form.Item>
        </Col>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.submitted_date")} name="submittedDate" rules={[yupSync]}>
            <DatePicker format={dateFormat} />
          </Form.Item>
        </Col>
      </Row>
          <Tabs
            defaultActiveKey="1"
            centered
            items={[
              {
                label: `DMPA-IM`,
                key: '1',
                children: <ClientDoseForm
                            new_client_under_25={data.contraception_clients.dmpa_im_New_client_Under_25}
                            new_client_over_25={data.contraception_clients.dmpa_im_New_client_Over_25}
                            old_client_under_25={data.contraception_clients.dmpa_im_Old_client_Under_25}
                            old_client_over_25={data.contraception_clients.dmpa_im_Old_client_Over_25}
                            consultation="DMPA_IM" 
                            callbackBtnClick={(client_info, doseCount) => handleDMPA_IMClient(client_info, doseCount)} 
                          />,
              },
              {
                label: `OC Pills`,
                key: '2',
                children: <ClientDoseForm
                            new_client_under_25={data.contraception_clients.oc_pill_New_client_Under_25}
                            new_client_over_25={data.contraception_clients.oc_pill_New_client_Over_25}
                            old_client_under_25={data.contraception_clients.oc_pill_Old_client_Under_25}
                            old_client_over_25={data.contraception_clients.oc_pill_Old_client_Over_25}
                            consultation="OC_PILL" 
                            callbackBtnClick={(client_info, doseCount) => handleOCPillsClient(client_info, doseCount)} 
                            />,
              }
            ]}
          />
      <Form.Item>
        <StyledFormAction>
          <SharedButton type="default" htmlType="button" size="middle" onClick={goBack}>
            {t("page.cancel")}
          </SharedButton>
          <SharedButton type="primary" htmlType="submit" size="middle">
            {t("page.create")}
          </SharedButton>
        </StyledFormAction>
      </Form.Item>
    </Form>
  );
});
const StyledFormAction = styled.div`
  display: flex;
  justify-content: flex-end;
  column-gap: 40px;
  margin-top: 24px;
`;
